import { useState } from 'react';

export function AttachmentSkeleton({ label }: { label: string }) {
	return (
		<span
			role='status'
			aria-label={label}
			className='absolute inset-0 rounded-lg bg-gray-200 dark:bg-gray-700 motion-safe:animate-pulse'
		>
			<span className='sr-only'>{label}</span>
		</span>
	);
}

// Keyed by source so a new URL always starts with its own loading state.
export function AttachmentImage(props: { src: string; name: string; className: string; thumbnail?: boolean }) {
	return <LoadingImage key={props.src} {...props} />;
}

function LoadingImage({
	src,
	name,
	className,
	thumbnail = false,
}: {
	src: string;
	name: string;
	className: string;
	thumbnail?: boolean;
}) {
	const [status, setStatus] = useState<'loading' | 'loaded' | 'error'>('loading');
	const [attempt, setAttempt] = useState(0);
	return (
		<span className={`relative block ${className}`} aria-busy={status === 'loading'}>
			<img
				key={attempt}
				src={src}
				alt={name}
				draggable={false}
				className={`block w-full h-full ${thumbnail ? 'object-cover' : 'object-contain'} ${
					status === 'loaded' ? '' : 'opacity-0'
				}`}
				ref={(image) => {
					// Cached images may finish before React receives their load event.
					if (image?.complete && image.naturalWidth > 0 && status === 'loading') setStatus('loaded');
				}}
				onLoad={() => setStatus('loaded')}
				onError={() => setStatus('error')}
			/>
			{status === 'loading' && <AttachmentSkeleton label={`Loading ${name}`} />}
			{status === 'error' && (
				<span className='absolute inset-0 flex flex-col items-center justify-center gap-2 rounded-lg bg-gray-100 p-2 text-center text-xs text-gray-600 dark:bg-gray-800 dark:text-gray-300'>
					<span role='status'>Image unavailable</span>
					{thumbnail ? (
						<span>Open to retry</span>
					) : (
						<button
							type='button'
							className='rounded px-2 py-1 text-blue-600 underline focus-visible:ring-2 focus-visible:ring-blue-500 dark:text-blue-400'
							onPointerDown={(event) => event.stopPropagation()}
							onClick={() => {
								setStatus('loading');
								setAttempt((value) => value + 1);
							}}
						>
							Retry image
						</button>
					)}
				</span>
			)}
		</span>
	);
}
